import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Alert,
  useTheme,
  Chip,
  TextField,
  Skeleton,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";
import { SERVICES_SEO_MAP } from "../data/servicesSeoData";

const MotionBox = motion(Box);

const SERVICES_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Digital Studio Services in Jaipur | WiYou! Tech Studios",
  "url": "https://studio.wiyoutech.in/services",
  "description": "Explore custom web development, UI/UX design, QR menu development, restaurant menu design, and custom software services by WiYou! Tech Studios in Jaipur, Rajasthan.",
  "provider": {
    "@type": "ProfessionalService",
    "name": "WiYou! Tech Studios",
    "url": "https://studio.wiyoutech.in",
    "telephone": "+917689919139",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Jaipur",
      "addressRegion": "Rajasthan",
      "addressCountry": "IN"
    }
  }
};

// Animation variant for fade up effect
const fadeUp600 = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export default function Services() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const handleNavigate = (serviceIdentifier) => {
    navigate(`/services/${serviceIdentifier}`);
  };

  // Real-time Data Fetching from Firestore
  useEffect(() => {
    const colRef = collection(db, "services");
    const q = query(colRef, orderBy("priority", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const servicesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setServices(servicesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Real-time services fetch error:", err);
        // Graceful fallback to core studio services
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Merge Firestore services with core studio offerings
  const allServices = useMemo(() => {
    const coreList = Object.values(SERVICES_SEO_MAP);
    if (!services || services.length === 0) {
      return coreList;
    }
    const firestoreSlugs = new Set(services.map((s) => s.slugId || s.id));
    const missingCore = coreList.filter((core) => !firestoreSlugs.has(core.slugId));
    return [...services, ...missingCore];
  }, [services]);

  // Unique categories for filter chips
  const categories = useMemo(() => {
    const cats = Array.from(
      new Set(
        allServices
          .map((s) => s.category)
          .filter((c) => typeof c === "string" && c.trim().length > 0)
      )
    );
    return ["All", ...cats];
  }, [allServices]);

  // Filtered services (by search + category)
  const filteredServices = useMemo(() => {
    return allServices.filter((s) => {
      const search = searchTerm.toLowerCase().trim();
      const matchesSearch =
        !search ||
        s.title?.toLowerCase().includes(search) ||
        (s.description || "").toLowerCase().includes(search) ||
        (s.subtitle || "").toLowerCase().includes(search);

      const matchesCategory =
        selectedCategory === "All" || s.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [allServices, searchTerm, selectedCategory]);

  // Skeleton list for loading
  const skeletonArray = Array.from({ length: 6 });

  return (
    <Container
      sx={{
        pt: 12,
        pb: 0,
        minHeight: "100vh",
      }}
    >
      <SEO
        title="Digital Studio Services in Jaipur | Web Development & QR Menus | WiYou!"
        description="Explore our comprehensive web development, UI/UX design, QR menu development, restaurant menu design, and custom software services in Jaipur & across India."
        keywords="web development company in Jaipur, website development services Jaipur, web design agency Jaipur, QR menu design Jaipur, digital menu development Jaipur, restaurant menu design Jaipur"
        canonicalUrl="https://studio.wiyoutech.in/services"
        structuredData={SERVICES_PAGE_SCHEMA}
      />
<MotionBox
  variants={fadeUp600}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true }}
  sx={{ mb: 6, mt: 10 }}
>
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: { xs: "flex-start", md: "center" },
      flexDirection: { xs: "column", md: "row" },
      gap: 3,
    }}
  >
    <Box>
      {/* Chip */}
      <Chip
        label="Services"
        size="small"
        sx={{
          mb: 2,
          borderRadius: 999,
          px: 1.5,
          py: 0.5,
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          backdropFilter: "blur(8px)",
          background:
            "linear-gradient(to right, rgba(255,255,255,0.09), rgba(255,255,255,0.04))",
          border: "1px solid rgba(255,255,255,0.15)",
        }}
      />

      {/* Title */}
      <Typography
        variant="h3"
        fontFamily="'Gilda Display', serif"
        fontWeight={700}
        sx={{
          letterSpacing: "-0.03em",
          lineHeight: 1.2,
          background:
            "linear-gradient(90deg, #000, #444, #000)",
          WebkitBackgroundClip: "text",
          color: "transparent",
          mb: 1,
        }}
      >
        Our Services
      </Typography>

      {/* Animated Underline */}
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: "72px" }}
        transition={{ duration: 0.6 }}
        style={{
          height: "3px",
          borderRadius: 10,
          background: theme.palette.text.primary,
          marginTop: "6px",
        }}
      />
    </Box>

    {/* Short Intro */}
    <Typography
      align="left"
      color="text.secondary"
      sx={{
        maxWidth: 440,
        fontSize: 15,
        lineHeight: 1.75,
      }}
    >
      Discover a curated suite of digital solutions tailored to elevate your
      brand — from design, development, and automation to deployment and
      long-term optimization.
    </Typography>
  </Box>
</MotionBox>

<MotionBox
  variants={fadeUp600}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true }}
  sx={{ mb: 7 }}
>
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      gap: 2,
      alignItems: { xs: "flex-start", md: "center" },
      justifyContent: "space-between",
    }}
  >
    {/* Search Bar */}
    <TextField
      size="small"
      placeholder="Search for a service…"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{
        width: { xs: "100%", md: 340 },
        backgroundColor: theme.palette.background.paper,
        borderRadius: 3,
        boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
        "& .MuiOutlinedInput-root": {
          borderRadius: 3,
        },
      }}
    />

    {/* Filters */}
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.2,
        alignItems: "center",
      }}
    >
      {categories.map((cat) => (
        <Chip
          key={cat}
          label={cat}
          clickable
          onClick={() => setSelectedCategory(cat)}
          size="small"
          sx={{
            borderRadius: 999,
            px: 1.6,
            py: 0.4,
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            transition: "0.25s ease",
            bgcolor:
              selectedCategory === cat
                ? theme.palette.text.primary
                : "rgba(255,255,255,0.02)",
            color:
              selectedCategory === cat
                ? theme.palette.getContrastText(theme.palette.text.primary)
                : theme.palette.text.secondary,

            border: `1px solid ${
              selectedCategory === cat
                ? theme.palette.text.primary
                : "rgba(0,0,0,0.15)"
            }`,

            "&:hover": {
              bgcolor:
                selectedCategory === cat
                  ? theme.palette.text.primary
                  : "rgba(255,255,255,0.07)",
            },
          }}
        />
      ))}
    </Box>
  </Box>
</MotionBox>

      {/* --- Error State --- */}
      {error && (
        <Alert severity="error" sx={{ my: 4 }}>
          {error}
        </Alert>
      )}

      {/* --- Skeleton Loading --- */}
      {loading && !error && (
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {skeletonArray.map((_, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  height: 420,
                  width: 360,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Skeleton variant="rectangular" height={200} />
                <Box sx={{ p: 3, flexGrow: 1 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="80%" />
                  <Box sx={{ mt: 3 }}>
                    <Skeleton variant="rectangular" width={120} height={32} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}  

      {/* --- No Services State --- */}
      {!loading && !error && filteredServices.length === 0 && (
        <Typography color="text.secondary" textAlign="center" py={4}>
          No services match your filters yet.
        </Typography>
      )}

      {/* --- Service Grid --- */}
      {!loading && !error && filteredServices.length > 0 && (
        <Grid container spacing={4} sx={{ mb: 10 }}>
          {filteredServices.map((s, i) => {
            const imageUrl =
              s.imageUrl || s.img || "/assets/services_default.jpg";

            return (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                {/* Outer motion for fade + tilt + hover state */}
                <MotionBox
                  variants={fadeUp600}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.06 }}
                >
                  {/* THIS is the single hover controller */}
<motion.div
  initial="rest"
  whileHover="hover"
  animate="rest"
  style={{ width: "100%" }}
>
  <motion.div
    style={{
      height: 420,
      width: 360,
      mx: "auto",
      borderRadius: 28,
      overflow: "hidden",
      position: "relative",
      cursor: "pointer",
      border: `0px solid ${theme.palette.divider}`,
      boxShadow: "none",
      backgroundColor: "#e9e9e9ff",
    }}
    onMouseMove={(e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 10;
      e.currentTarget.style.transform = `perspective(900px) rotateX(${-y}deg) rotateY(${x}deg) translateY(-6px)`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform =
        "perspective(900px) rotateX(0deg) rotateY(0deg) translateY(0px)";
    }}
    onClick={() => handleNavigate(s.id)}
  >
    {/* Background image zoom effect */}
    <motion.div
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "brightness(0.9)",
        border: "none"
      }}
      variants={{
        rest: { scale: 1 },
        hover: { scale: 1.14, y: -6 },
      }}
      transition={{ duration: 0.65, ease: [0.22, 0.61, 0.36, 1] }}
    />

<motion.div
  initial="rest"
  animate="rest"
  whileHover="hover"
  style={{
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "28px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
      background:
        "linear-gradient(to top, rgba(50, 50, 50, 0.35) 0%, rgba(50, 50, 50, 0.15) 60%, rgba(50, 50, 50, 0))",
    justifyContent: "flex-end",
    zIndex: 3,
  }}
>

  {/* ---- BLUR SHADOW ON HOVER ---- */}
  <motion.div
    variants={{
      rest: { opacity: 0, y: 40 },
      hover: { opacity: 1, y: 0 },
    }}
    transition={{ duration: 0.45 }}
    style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "100%",
      background:
        "linear-gradient(to top, rgba(50, 50, 50, 0.35) 0%, rgba(50, 50, 50, 0.15) 60%, rgba(50, 50, 50, 0))",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      zIndex: 2,
      pointerEvents: "none",
    }}
  />

{/* ---- Title + Description Animated Stack ---- */}
<motion.div
  variants={{
    rest: { y: 40, opacity: 1 },
    hover: { y: -8, opacity: 1 },
  }}
  transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
  style={{
    position: "relative",
    zIndex: 4,
    width: "100%",
    paddingRight: "8px",
  }}
>
  {/* Title */}
  <motion.div
    variants={{
      rest: { opacity: 1, y: 0 },
      hover: { opacity: 1, y: -6 },
    }}
    transition={{ duration: 0.45, ease: "easeOut" }}
  >
    <Typography
      variant="h2"
      sx={{
        color: "#fff",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: "-0.01em",
        fontSize: "1.8rem",
      }}
    >
      {s.title}
    </Typography>
  </motion.div>

  {/* Description (hover reveal) */}
  <motion.div
    variants={{
      rest: { opacity: 0, y: 22, maxHeight: 0 },
      hover: { opacity: 1, y: 0, maxHeight: 400 },
    }}
    transition={{
      duration: 0.45,
      delay: 0.1,
      ease: [0.25, 0.1, 0.25, 1],
    }}
    style={{
      overflow: "hidden",
      width: "100%",
    }}
  >
    <Typography
      variant="body2"
      sx={{
        mt: 1,
        color: "rgba(255,255,255,0.94)",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.55,
        letterSpacing: "0.015em",
      }}
    >
      {s.description ||
        "Learn more about this service and how it can help your business grow."}
    </Typography>
  </motion.div>
</motion.div>


  {/* ---- Button Reveal ---- */}
  <motion.div
    variants={{
      rest: { opacity: 0, y: 25 },
      hover: { opacity: 1, y: 0 },
    }}
    transition={{ duration: 0.40, delay: 0.12 }}
    style={{ position: "relative", zIndex: 4 }}
  >
    <Button
      variant="contained"
      size="small"
      sx={{
        bgcolor: "#ffffffbc",
        color: "#111",
        borderRadius: 999,
        textTransform: "uppercase",
        fontSize: 11,
        px: 3,
        py: 0.9,
        letterSpacing: "0.12em",
        boxShadow: "none",
        backdropFilter: "blur(40px)",
        transition: "all 0.2s ease-out",
        "&:hover": { boxShadow: "none", bgcolor: "#0000008c", color: "#fff", backdropFilter: "blur(60px)"},
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleNavigate(s.id);
      }}
    >
      Get Started
    </Button>
  </motion.div>

</motion.div>

  </motion.div>
</motion.div>

                </MotionBox>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* --- Why Choose Us Section --- */}
{/* ---- WHY CHOOSE US ---- */}
<Box
  sx={{
    mt: 10,
    pt: 10,
    pb: 16,
    borderTop: `1px solid ${theme.palette.divider}`,
    backdropFilter: "blur(4px)",
  }}
>
  {/* Header */}
  <MotionBox
    variants={fadeUp600}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    sx={{ mb: 6 }}
  >
    <Typography
      variant="h4"
      align="center"
      fontFamily="'Gilda Display', serif"
      fontWeight={700}
      sx={{
        letterSpacing: "-0.02em",
        mb: 1,
      }}
    >
      Why Choose Us?
    </Typography>

    <Typography
      variant="body2"
      align="center"
      color="text.secondary"
      sx={{
        maxWidth: 520,
        mx: "auto",
        lineHeight: 1.75,
        opacity: 0.85,
        fontSize: 15,
      }}
    >
      We don't just ship websites — we build long-term digital systems that
      support your growth, performance, and reliability.
    </Typography>
  </MotionBox>

  {/* Cards Grid */}
  <Grid container spacing={4}>
    {[
      { icon: "🌟", title: "Tailor-Made Solutions", delay: 0 },
      { icon: "💻", title: "Innovative Technology", delay: 0.1 },
      { icon: "🎨", title: "User-Centric Design", delay: 0.2 },
      { icon: "🤝", title: "Dedicated Support", delay: 0.3 },
      { icon: "💰", title: "Cost-Effective Packages", delay: 0.4 },
      { icon: "🚀", title: "Fast Delivery", delay: 0.5 },
      { icon: "📊", title: "Performance Analytics", delay: 0.6 },
      { icon: "🔒", title: "Secure Solutions", delay: 0.7 },
      { icon: "📱", title: "Mobile Optimization", delay: 0.8 },
    ].map((g, idx) => (
      <Grid item xs={12} sm={6} md={4} key={idx}>
        <MotionBox
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: g.delay }}
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Card */}
          <motion.div
            whileHover={{
              y: -8,
              scale: 1.02,
              boxShadow: "0 18px 42px rgba(0,0,0,0.18)",
            }}
            transition={{ duration: 0.35 }}
            style={{
              borderRadius: 20,
              width: 360,
              mx: "auto",
              padding: "28px 24px",
              textAlign: "center",
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.07)",
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                fontSize: 38,
                mb: 2,
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                width: 68,
                height: 68,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                backdropFilter: "blur(6px)",
              }}
            >
              {g.icon}
            </Box>

            {/* Title */}
            <Typography
              variant="h6"
              fontFamily="'Gilda Display', serif"
              sx={{
                fontWeight: 600,
                mb: 1.4,
                fontSize: 20,
                letterSpacing: "-0.01em",
              }}
            >
              {g.title}
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: 14.5,
                lineHeight: 1.7,
                opacity: 0.85,
              }}
            >
              {g.title === "Tailor-Made Solutions"
                ? "We craft highly customized experiences built around your brand, needs, and vision."
                : "We help your business scale with reliable, modern, and performance-driven solutions."}
            </Typography>
          </motion.div>
        </MotionBox>
      </Grid>
    ))}
  </Grid>
</Box>

<ContactForm sx={{ pt: 10 }} />

    </Container>
  );
}
